import React from 'react';
import { Modal, Tag } from 'antd';

// Shared building blocks for the Teams v3 single-select picker so the inline
// column picker (buildReferralColumns), the bulk-action toolbar (ReferralActionBar)
// and the record-viewer slideout (RecordDetails/TeamsInfo) all present an identical
// team UX and share one reassign-confirm policy. Team option value = team.id
// (which is the team slug on the backend).

// Sentinel option value for the "No team" (clear) entry.
export const NO_TEAM_VALUE = '__no_team__';

// Color-coded team chip (mirrors the label chip style). `extraStyle` lets callers
// tweak spacing to match their surface without duplicating the color logic.
export const teamChip = (team, extraStyle = {}) => (
  <Tag
    style={{
      backgroundColor: team.color,
      color: team.is_dark_text ? 'black' : 'white',
      margin: 0,
      ...extraStyle,
    }}
  >
    {team.name}
  </Tag>
);

// antd Select/Menu options: a "No team" clear entry followed by one chip per team.
export const buildTeamOptions = (teams = []) => ([
  {
    value: NO_TEAM_VALUE,
    name: 'No team',
    label: <span className="referral-team-cell__clear">No team</span>,
  },
  ...teams.map((team) => ({
    value: team.id,
    name: team.name,
    label: teamChip(team),
  })),
]);

// True only when applying `nextTeamSlug` to `record` is a real cross-team
// reassignment that would cost the current user visibility: the record must warn,
// already belong to a team, and the target must be a DIFFERENT real team. First-time
// sets (no current team) and clears (null target) never warn.
export const teamReassignNeedsConfirm = ({ record, nextTeamSlug }) => {
  if (!record || record.team_reassign_visibility_warning !== true) return false;
  const currentSlug = record.team ? record.team.id : null;
  if (!currentSlug) return false;
  const next = nextTeamSlug || null;
  if (next === null) return false;
  return next !== currentSlug;
};

// Task F: resolves true when it's safe to proceed with the team set. Shows an
// antd Modal.confirm only for a real visibility-losing reassignment; otherwise
// resolves true immediately so callers can stay synchronous in the common case.
export const confirmTeamReassign = ({ record, nextTeamSlug }) => {
  if (!teamReassignNeedsConfirm({ record, nextTeamSlug })) return Promise.resolve(true);
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Reassign to another team?',
      content: 'You\'re not in this document\'s department, so you\'ll lose access to it after reassigning.',
      okText: 'Reassign anyway',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

// Bulk eligibility: the team control is only enabled when EVERY selected record
// is editable. An empty selection is not eligible.
export const allSelectedTeamEditable = (records = []) => (
  records.length > 0 && records.every((record) => record && record.can_edit_team === true)
);
